import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, ArrowLeft, X, MailQuestion } from "lucide-react";
import { api, setTokens, formatApiError } from "../lib/api";

const inputClass =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-[#00E5FF]/60";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setTokens(data.access_token, data.refresh_token);
      toast.success("Login realizado com sucesso.");
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err, "Não foi possível entrar. Verifique os dados."));
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setForgotEmail(email);
    setForgotSent(false);
    setForgotError("");
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setForgotError(formatApiError(err, "Não foi possível enviar o e-mail. Tente novamente."));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-6">
      <div className="bg-grid absolute inset-0 opacity-50 [mask-image:radial-gradient(ellipse_at_center,black_25%,transparent_70%)]" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-[#5B8CFF]/10 blur-[130px]" />

      <div className="gradient-border relative w-full max-w-md rounded-3xl p-8 sm:p-10" data-testid="admin-login-card">
        <div className="flex items-center gap-3">
          <span className="relative block h-10 w-10 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] p-[1.5px]">
            <span className="flex h-full w-full items-center justify-center rounded-[7px] bg-[#050508]">
              <span className="text-gradient font-display text-xl font-black">E</span>
            </span>
          </span>
          <div className="leading-none">
            <span className="block font-display text-base font-bold tracking-[0.18em]">EDALIX</span>
            <span className="mt-1 block text-[9px] font-medium tracking-[0.42em] text-white/50">
              PAINEL ADMINISTRATIVO
            </span>
          </div>
        </div>

        <h1 className="mt-8 font-display text-2xl font-bold tracking-tight">Acessar painel</h1>
        <p className="mt-2 text-sm font-light text-white/55">
          Entre com suas credenciais de administrador.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label htmlFor="admin-email" className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
              E-MAIL
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              autoComplete="email"
              placeholder="voce@edalix.com.br"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
              SENHA
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,filter,opacity] duration-300 hover:scale-[1.01] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            {loading ? "Entrando..." : "Entrar"}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={openForgot}
              data-testid="admin-forgot-link"
              className="text-xs font-medium text-white/45 transition-colors duration-300 hover:text-[#00E5FF]"
            >
              Esqueci minha senha
            </button>
          </div>
        </form>

        <div className="mt-8 text-center">
          <Link
            to="/"
            data-testid="admin-login-back-link"
            className="inline-flex items-center gap-2 text-xs font-medium text-white/45 transition-colors duration-300 hover:text-[#00E5FF]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Voltar para o site
          </Link>
        </div>
      </div>

      {forgotOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#030305]/90 p-4 backdrop-blur-xl"
          data-testid="admin-forgot-modal"
        >
          <div className="gradient-border w-full max-w-md rounded-3xl p-7 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-bold tracking-tight">Recuperar senha</h2>
                <p className="mt-1 text-xs font-light text-white/50">
                  Enviaremos um link de redefinição para o seu e-mail.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForgotOpen(false)}
                data-testid="admin-forgot-close-button"
                aria-label="Fechar"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {forgotSent ? (
              <div className="mt-6" data-testid="admin-forgot-success">
                <div className="flex items-start gap-3 rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-4 py-4">
                  <MailQuestion className="mt-0.5 h-5 w-5 shrink-0 text-[#00E5FF]" />
                  <p className="text-sm font-light leading-relaxed text-white/80">
                    Se o e-mail estiver cadastrado, você receberá o link de redefinição em
                    instantes. O link é válido por 30 minutos. Verifique também a caixa de spam.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setForgotOpen(false)}
                  data-testid="admin-forgot-done-button"
                  className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
                >
                  Entendi
                </button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="mt-6 space-y-4">
                <div>
                  <span className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
                    E-MAIL DO ADMINISTRADOR
                  </span>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="voce@edalix.com.br"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    data-testid="admin-forgot-email-input"
                    className={inputClass}
                  />
                </div>
                {forgotError && (
                  <p data-testid="admin-forgot-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {forgotError}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={forgotLoading}
                  data-testid="admin-forgot-submit-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white transition-[filter,opacity] duration-300 hover:brightness-110 disabled:opacity-60"
                >
                  {forgotLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {forgotLoading ? "Enviando..." : "Enviar link de redefinição"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogin;
